import { ArrowLeft, ArrowRight, ArrowUpRight, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const categories = [{
  label: "ALL",
  active: true
}, {
  label: "ADVOCACY",
  active: false
}, {
  label: "COMMUNITY",
  active: false
}, {
  label: "RESOURCES",
  active: false
}, {
  label: "UPDATES",
  active: false
}, {
  label: "STORIES",
  active: false
}];
const fallbackPosts = [{
  id: "f1",
  title: "Bridging the Gender Pay Gap in Modern Workplaces",
  excerpt: "An in-depth look at systemic barriers to equal pay and actionable strategies for organizations to foster true financial equity for all employees.",
  category: "ADVOCACY",
  date: "2024.11.03",
  author: "S. RIVERA",
  readTime: "6 MIN READ",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/gender_pay_gap_analysis.webp",
  height: "tall"
}, {
  id: "f2",
  title: "New Mentorship Initiative: Empowering Future Leaders",
  excerpt: "Our latest community program connects emerging female leaders with seasoned mentors to drive professional growth and long-term success.",
  category: "UPDATES",
  date: "2024.10.28",
  author: "J. SMITH",
  readTime: "4 MIN READ",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/mentorship_program_launch.png",
  height: "short"
}, {
  id: "f3",
  title: "Case Study: Transforming Local Policy for Equality",
  excerpt: "How our grassroots advocacy efforts successfully influenced local legislation to protect reproductive rights and healthcare access.",
  category: "STORIES",
  date: "2024.10.21",
  author: "L. CHEN",
  readTime: "8 MIN READ",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/community_advocacy_success.png",
  height: "medium"
}, {
  id: "f4",
  title: "Understanding Intersectionality in Social Movements",
  excerpt: "A guide to recognizing how race, class, and gender overlap to create unique challenges, and why inclusive advocacy is essential.",
  category: "RESOURCES",
  date: "2024.10.15",
  author: "S. RIVERA",
  readTime: "5 MIN READ",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/intersectionality_guide.png",
  height: "medium"
}, {
  id: "f5",
  title: "Tooling Update: New Digital Advocacy Dashboard",
  excerpt: "We have launched a new interactive portal to help supporters track legislative progress and contact their representatives directly.",
  category: "COMMUNITY",
  date: "2024.10.08",
  author: "J. SMITH",
  readTime: "3 MIN READ",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/advocacy_dashboard_tool.png",
  height: "short"
}, {
  id: "f6",
  title: "Lessons from a Year of Global Equality Campaigns",
  excerpt: "Reflecting on the milestones achieved in our international awareness efforts and the path forward for global gender parity.",
  category: "ADVOCACY",
  date: "2024.10.01",
  author: "L. CHEN",
  readTime: "7 MIN READ",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/global_equality_campaign.png",
  height: "tall"
}];
function JournalGrid() {
  const {
    posts,
    loading,
    error,
    currentPage,
    totalPages,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    isRefetching
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="border border-border bg-card rounded-lg shadow-md animate-pulse">
            <div className="aspect-[4/5] bg-muted/40 rounded-t-lg" />
            <div className="p-6 space-y-3">
              <div className="h-4 w-3/4 bg-muted/40" />
              <div className="h-3 w-full bg-muted/30" />
              <div className="h-3 w-2/3 bg-muted/30" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="border border-border p-12 text-center rounded-lg bg-card">
        <p className="text-sm text-muted-foreground font-mono">// ERROR_LOADING_ENTRIES</p>
      </div>;
  }
  const displayPosts = posts.length > 0 ? posts : fallbackPosts.map(p => ({
    id: p.id,
    title: p.title,
    excerpt: p.excerpt,
    link: "#",
    date: p.date,
    featuredImage: p.image,
    categories: [{
      id: 1,
      name: p.category,
      slug: "",
      taxonomy: "category"
    }],
    author: {
      id: 1,
      name: p.author
    },
    customFields: {
      readTime: p.readTime,
      height: p.height
    }
  }));
  const formatDate = (d?: string) => {
    if (!d) return "";
    try {
      const date = new Date(d);
      if (!isNaN(date.getTime())) {
        return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, "0")}.${String(date.getDate()).padStart(2, "0")}`;
      }
    } catch {}
    return d;
  };
  return <>
      <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
        {displayPosts.map((post, i) => {
        const heights = ["aspect-[4/5]", "aspect-[16/10]", "aspect-[4/5]", "aspect-[16/11]", "aspect-[16/10]", "aspect-[4/5]"];
        const aspect = heights[i % heights.length];
        const category = post.categories?.[0]?.name || "ENTRY";
        const authorName = typeof post.author === "object" ? post.author?.name : post.author;
        const readTime = (post as any).customFields?.readTime || "5 MIN READ";
        return <article key={post.id} data-index={i} className="group break-inside-avoid mb-6 border border-border bg-card rounded-lg shadow-md hover:border-primary transition-all duration-300 relative overflow-hidden">
              <div className="absolute -top-px -left-px z-20 text-primary">
                <Plus className="w-3 h-3" strokeWidth={1.5} />
              </div>
              <div className="absolute -top-px -right-px z-20 text-primary">
                <Plus className="w-3 h-3" strokeWidth={1.5} />
              </div>

              <Link to={post.link || "#"} className="block">
                <div className={`relative ${aspect} overflow-hidden bg-muted rounded-t-lg`}>
                  {post.featuredImage && <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />}
                  <div className="absolute top-3 left-3">
                    <Badge variant="default" className="font-default text-[10px] tracking-wider bg-background/80 text-primary border border-primary/40 backdrop-blur-sm rounded-md">
                      {category.toUpperCase()}
                    </Badge>
                  </div>
                  <div className="absolute top-3 right-3">
                    <span className="font-mono text-[10px] tracking-wider text-foreground bg-background/80 px-2 py-1 backdrop-blur-sm border border-border rounded-md">
                      {formatDate(post.date)}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="font-default text-[22px] font-semibold leading-snug text-foreground group-hover:text-primary transition-colors duration-300">
                    {post.title}
                  </h3>
                  {post.excerpt && <p className="mt-3 text-sm text-muted-foreground leading-relaxed line-clamp-3 font-default">
                      {post.excerpt.replace(/<[^>]*>/g, "")}
                    </p>}

                  <div className="mt-5 pt-4 border-t border-border flex items-center justify-between">
                    <span className="font-mono text-[11px] tracking-wider text-muted-foreground uppercase">
                      {authorName || "FOREVER FRESH"} · {readTime}
                    </span>
                    <span className="font-mono text-[11px] tracking-wider text-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-1">
                      <ArrowUpRight className="w-3 h-3" strokeWidth={2} />
                      READ
                    </span>
                  </div>
                </div>
              </Link>
            </article>;
      })}
      </div>

      {(posts.length > 0 || totalPages > 1) && <div className="mt-16 pt-8 border-t border-border flex items-center justify-between">
          <div className="font-mono text-xs tracking-wider text-muted-foreground">
            <span className="text-primary">{String(currentPage).padStart(2, "0")}</span>
            <span className="mx-2 text-border">/</span>
            <span>{String(Math.max(totalPages, 1)).padStart(2, "0")}</span>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={prevPage} disabled={!hasPrev || isRefetching} className="border-border hover:border-primary hover:text-primary hover:bg-transparent disabled:opacity-30 rounded-lg" aria-label="Previous page">
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            </Button>
            <Button variant="outline" size="icon" onClick={nextPage} disabled={!hasNext || isRefetching} className="border-border hover:border-primary hover:text-primary hover:bg-transparent disabled:opacity-30 rounded-lg" aria-label="Next page">
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>}
    </>;
}
export default function Womensequalitynewsgrid() {
  const wp_query_params = {
    post_type: "post",
    per_page: 9,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="4629" id="womensequalitynewsgrid" className="bg-background text-foreground pt-24 pb-32 px-4 sm:px-6 lg:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="mb-16">
          

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-7">
              <h1 className="font-default text-5xl sm:text-6xl lg:text-[6rem] leading-[0.95] tracking-tight text-foreground">
                Awareness.
              </h1>
            </div>
            <div className="lg:col-span-5">
              <p className="text-base text-muted-foreground leading-relaxed font-default max-w-md">
                Keeping awareness issues forever fresh in people’s minds through education, community action, and purpose-driven advocacy.
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-b border-border py-5 mb-12 -mx-4 sm:-mx-6 lg:-mx-12 px-4 sm:px-6 lg:px-12">
          <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto scrollbar-hide">
            <span className="font-mono text-[10px] tracking-widest text-muted-foreground whitespace-nowrap mr-2 hidden sm:inline">
              FILTER:
            </span>
            {categories.map((cat, i) => <Button key={cat.label} data-index={i} variant={cat.active ? "default" : "outline"} size="sm" className={cat.active ? "h-8 px-4 font-mono text-[11px] tracking-wider bg-primary text-primary-foreground hover:bg-primary/90 border-0 whitespace-nowrap rounded-lg" : "h-8 px-4 font-mono text-[11px] tracking-wider border-border text-muted-foreground hover:text-primary hover:border-primary bg-transparent hover:bg-transparent whitespace-nowrap rounded-lg"}>
                {cat.label}
              </Button>)}
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 9,
        orderby: "date",
        order: "desc"
      }}>
          <JournalGrid />
        </PostsProvider>
      </div>
    </section>;
}